import { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CircularProgress, FormControl, FormLabel,
    Modal, ModalDialog, Option, Select, Stack, Typography,
} from '@mui/joy';

import { APIPlacesCities, APIPlacesCategories, APIPlaces, APIDateTreatClaim } from '../api_date_treat';

export default function DateTreatPickerModal({ open, onClose, token, amount, onClaimed }) {
    const [cities, setCities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [places, setPlaces] = useState([]);
    const [cityId, setCityId] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [placeId, setPlaceId] = useState(null);
    const [loadingCat, setLoadingCat] = useState(false);
    const [loadingPlaces, setLoadingPlaces] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const [error, setError] = useState(null);

    // Города при открытии.
    useEffect(() => {
        if (!open || !token) return;
        setError(null);
        APIPlacesCities(token)
            .then((r) => {
                const list = r?.items || [];
                setCities(list);
                if (list.length === 1) setCityId(String(list[0].id)); // один город — сразу выбран
            })
            .catch(() => {});
    }, [open, token]);

    // Город → категории.
    useEffect(() => {
        setCategoryId(''); setCategories([]); setPlaces([]); setPlaceId(null);
        if (!cityId || !token) return;
        setLoadingCat(true);
        APIPlacesCategories(token, cityId)
            .then((r) => setCategories(r?.items || []))
            .catch(() => {})
            .finally(() => setLoadingCat(false));
    }, [cityId, token]);

    // Категория → места.
    useEffect(() => {
        setPlaces([]); setPlaceId(null);
        if (!cityId || !categoryId || !token) return;
        setLoadingPlaces(true);
        APIPlaces(token, { cityId, categoryId })
            .then((r) => setPlaces(r?.items || []))
            .catch(() => {})
            .finally(() => setLoadingPlaces(false));
    }, [cityId, categoryId, token]);

    const claim = async () => {
        if (!placeId) return;
        setClaiming(true); setError(null);
        try {
            const r = await APIDateTreatClaim(token, placeId);
            onClaimed(r.treat);
        } catch (e) {
            setError(e?.response?.data?.error || 'Не удалось получить сертификат');
        } finally {
            setClaiming(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 520, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                <Typography level="h4">Куда пойдёте?</Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                    Выберите город, тип и место — сертификат на {Math.round(amount)} ₽ будет на него.
                </Typography>
                {error && <Alert color="danger" sx={{ mb: 1 }}>{error}</Alert>}

                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Город</FormLabel>
                        <Select placeholder="Выберите город" value={cityId}
                                onChange={(_, v) => setCityId(v ?? '')}>
                            {cities.map((c) => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
                        </Select>
                        {cities.length === 0 && (
                            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                Пока нет городов с местами.
                            </Typography>
                        )}
                    </FormControl>

                    {cityId && (
                        <FormControl>
                            <FormLabel>Чем займётесь</FormLabel>
                            <Select placeholder="Выберите тип" value={categoryId}
                                    onChange={(_, v) => setCategoryId(v ?? '')}>
                                {categories.map((c) => (
                                    <Option key={c.id} value={String(c.id)}>
                                        {c.icon ? `${c.icon} ` : ''}{c.name}
                                    </Option>
                                ))}
                            </Select>
                            {loadingCat && (
                                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>Загрузка…</Typography>
                            )}
                        </FormControl>
                    )}

                    {categoryId && (
                        <Box>
                            <FormLabel sx={{ mb: 1 }}>Место</FormLabel>
                            {loadingPlaces ? (
                                <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size="sm" /></Stack>
                            ) : places.length === 0 ? (
                                <Typography level="body-sm" color="neutral">
                                    В этом городе пока нет таких мест.
                                </Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {places.map((p) => {
                                        const selected = placeId === p.id;
                                        return (
                                            <Card key={p.id}
                                                  variant={selected ? 'solid' : 'outlined'}
                                                  color={selected ? 'primary' : 'neutral'}
                                                  onClick={() => setPlaceId(p.id)}
                                                  sx={{ cursor: 'pointer', p: 1.5 }}>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    {p.image_url && (
                                                        <img src={p.image_url} alt="" width={48} height={48}
                                                             style={{ objectFit: 'cover', borderRadius: 8 }} />
                                                    )}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                                                        {p.address && <Typography level="body-xs">{p.address}</Typography>}
                                                    </Box>
                                                </Stack>
                                            </Card>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    )}

                    <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                        <Button loading={claiming} disabled={!placeId} onClick={claim}>
                            Получить сертификат
                        </Button>
                        <Button variant="outlined" color="neutral" onClick={onClose}>Отмена</Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}